/* STEP 1 — Pick partner + a single account code (real data, card UI).
   Account codes come from courierPartner/searchCached (the non-cached /search can
   return empty on some envs). Partner cards (single-select) → the partner's codes as
   a card list (one at a time). The chosen code carries its prozoId, the upload key
   for the rate/TAT steps. */
import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined'
import SearchIcon from '@mui/icons-material/Search'
import { useLazySearchCouriersCachedQuery } from '../../../redux/supplyApiSlice'
import { apiError } from '../supplyMeta'
import { normalizeCourier } from '../m1-codes/codeData'
import { Badge, initials } from '../components'
import { VertChips } from './DeltaPanel'
import WizardFooter from './WizardFooter'

/* soft tonal avatar palette (light tint bg + deep same-hue initials) — modern & calm */
const AVATAR_TONES = [
  {bg: '#E0E7FF', fg: '#4338CA'}, // indigo
  {bg: '#DBEAFE', fg: '#1D4ED8'}, // blue
  {bg: '#CCFBF1', fg: '#0F766E'}, // teal
  {bg: '#DCFCE7', fg: '#15803D'}, // green
  {bg: '#FEF9C3', fg: '#A16207'}, // amber
  {bg: '#FFE4E6', fg: '#BE123C'}, // rose
  {bg: '#F3E8FF', fg: '#7E22CE'}, // violet
  {bg: '#FFEDD5', fg: '#C2410C'}, // orange
]
const toneFor = (s) => {
  let h = 0
  const str = String(s || '')
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) % 997
  return AVATAR_TONES[h % AVATAR_TONES.length]
}

export default function StepPick({ U, onPickParent, onPickCode, onNext, toast, initialCode }) {
  const [codes, setCodes] = useState([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')
  const [pq, setPq] = useState('')
  const [fetchCouriers] = useLazySearchCouriersCachedQuery()

  useEffect(() => {
    let on = true
    setLoading(true)
    setErr('')
    fetchCouriers({
      offset: 0,
      limit: 500,
      skipCount: true,
      andfilter: [{ field: 'is_deleted', operator: 'eq', value: '0' }],
      includes: 'id,name,parent,cpAccountCode,prozoId,supportedShipmentType,type,serviceTypePrimary,serviceTypeSecondary,isActive',
    })
      .unwrap()
      .then((result) => {
        if (!on) return
        const docs = Array.isArray(result) ? result : result?.data || []
        const rows = docs.map(normalizeCourier).filter((c) => c.code && c.prozoId)
        setCodes(rows)
        // deep-link (?code=) → preselect the parent + code once the list loads
        if (initialCode && !U.code) {
          const c = rows.find((x) => x.code === initialCode)
          if (c) {
            onPickParent(c.parent)
            onPickCode({ code: c.code, prozoId: c.prozoId, display: c.display })
          }
        }
      })
      .catch((e) => on && setErr(apiError(e)))
      .finally(() => on && setLoading(false))
    return () => {
      on = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchCouriers])

  /* group the flat code list into parents (the cards) */
  const partners = useMemo(() => {
    const m = new Map()
    codes.forEach((c) => {
      if (!c.parent) return
      const e = m.get(c.parent) || { name: c.parent, count: 0, verticals: new Set() }
      e.count++
      e.verticals.add(c.vertical)
      m.set(c.parent, e)
    })
    return [...m.values()]
      .map((e) => ({ ...e, verticals: [...e.verticals] }))
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [codes])

  const filteredPartners = useMemo(() => {
    const t = pq.trim().toLowerCase()
    return t ? partners.filter((p) => p.name.toLowerCase().includes(t)) : partners
  }, [partners, pq])

  const parent = U.partnerName || ''
  const visibleCodes = useMemo(
    () => (parent ? codes.filter((c) => c.parent === parent).sort((a, b) => (a.code || '').localeCompare(b.code || '')) : []),
    [codes, parent]
  )

  const validate = () => {
    if (!parent) {
      toast('Select a parent courier first.', 'error')
      return false
    }
    if (!U.code) {
      toast('Select the account code to update.', 'error')
      return false
    }
    return true
  }

  return (
    <>
      <div className='card card-pad'>
        <h3 style={{ margin: '0 0 6px' }}>Which courier and code are you updating?</h3>
        <p className='sub' style={{ margin: '0 0 18px' }}>
          Pick a courier partner, then select the account code whose rates / TAT you want to revise.
          One code at a time. Partners without account codes are hidden.
        </p>

        {!loading && !err && partners.length ? (
          <div
            style={{display: 'flex', alignItems: 'center', gap: 8, border: '1px solid var(--border)', borderRadius: 8, padding: '0 12px', height: 40, maxWidth: 360, marginBottom: 14, background: '#fff'}}
          >
            <SearchIcon style={{width: 17, height: 17, color: 'var(--neutral)', flexShrink: 0}} />
            <input
              className='input'
              style={{border: 'none', outline: 'none', padding: 0, height: '100%', background: 'transparent', flex: 1}}
              placeholder='Search courier partner…'
              value={pq}
              onChange={(e) => setPq(e.target.value)}
            />
          </div>
        ) : null}

        {loading ? (
          <div className='muted' style={{ padding: '24px 4px', fontSize: 13 }}>Loading couriers…</div>
        ) : err ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 9,
              padding: '12px 14px',
              borderRadius: 10,
              fontSize: 12.5,
              background: '#FEF2F2',
              border: '1px solid #FECACA',
              color: '#B42318',
            }}
          >
            <InfoOutlinedIcon style={{ width: 16, height: 16, flexShrink: 0, marginTop: 1, color: '#DC2626' }} />
            <span>{err}</span>
          </div>
        ) : !partners.length ? (
          <div className='muted' style={{ padding: '24px 4px', fontSize: 13 }}>No courier partners with account codes yet.</div>
        ) : filteredPartners.length ? (
          <div className='uw-pgrid' style={{ maxHeight: 460, overflowY: 'auto', paddingRight: 4 }}>
            {filteredPartners.map((p, i) => {
              const on = parent === p.name
              return (
                <div
                  key={p.name}
                  className={'uw-pcard' + (on ? ' sel' : '')}
                  style={{ animationDelay: i * 30 + 'ms' }}
                  role='button'
                  tabIndex={0}
                  aria-pressed={on}
                  onClick={() => onPickParent(p.name)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      onPickParent(p.name)
                    }
                  }}
                >
                  <span className='pc-check'>
                    <CheckCircleIcon style={{ width: 20, height: 20 }} />
                  </span>
                  <div className='pc-top'>
                    <span className='pc-av' style={{ background: toneFor(p.name).bg, color: toneFor(p.name).fg }}>
                      {initials(p.name)}
                    </span>
                    <div style={{ minWidth: 0 }}>
                      <div className='pc-name'>{p.name}</div>
                      <div className='pc-legal'>Courier partner</div>
                    </div>
                  </div>
                  <div className='pc-foot'>
                    <span className='uw-codepill'>
                      <LocalShippingOutlinedIcon style={{ width: 13, height: 13 }} />
                      {p.count} account code{p.count > 1 ? 's' : ''}
                    </span>
                    <VertChips verticals={p.verticals} />
                  </div>
                </div>
              )
            })}
          </div>
        ) : (
          <div className='muted' style={{ padding: '24px 4px', fontSize: 13 }}>No courier partners match “{pq}”.</div>
        )}

        {parent ? (
          <>
            <div
              className='row between center'
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                margin: '26px 0 10px',
                flexWrap: 'wrap',
                gap: 8,
              }}
            >
              <span className='seclabel' style={{ margin: 0 }}>
                Account code
              </span>
              <span className='muted' style={{ fontSize: 12 }}>
                Select one code to revise
              </span>
            </div>
            <div className='uw-clist' style={{ maxHeight: 480, overflowY: 'auto' }}>
              <div className='uw-cgrid'>
                {visibleCodes.map((c) => {
                  const sel = U.code === c.code
                  return (
                    <div
                      key={c.code}
                      className={'uw-ccard' + (sel ? ' sel' : '')}
                      role='button'
                      tabIndex={0}
                      aria-pressed={sel}
                      onClick={() => onPickCode({ code: c.code, prozoId: c.prozoId, display: c.display })}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          onPickCode({ code: c.code, prozoId: c.prozoId, display: c.display })
                        }
                      }}
                    >
                      <span className='cc-check'>
                        <CheckCircleIcon style={{ width: 19, height: 19 }} />
                      </span>
                      {console.log(c, 'active')}
                      <div className='cc-code' title={c.code}>
                        {c.code}
                      </div>
                      <div className='cc-name' title={c.display}>
                        {c.display}
                      </div>
                      <div className='cc-chips'>
                        <VertChips verticals={[c.vertical]} />
                        <Badge status={c?.isActive ? "Active" : "InActive"} />
                        {c.prozoId ? (
                          <span className='tag tag-mono' title='prozoId (upload key)'>
                            {c.prozoId}
                          </span>
                        ) : null}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          </>
        ) : null}
      </div>

      <WizardFooter
        showBack={false}
        last={false}
        onNext={() => {
          if (validate()) onNext()
        }}
      />
    </>
  )
}

StepPick.propTypes = {
  U: PropTypes.object,
  onPickParent: PropTypes.func,
  onPickCode: PropTypes.func,
  onNext: PropTypes.func,
  toast: PropTypes.func,
  initialCode: PropTypes.string,
}
